// Pure helpers for checkpoints, resuming and handing off runs
// (docs/plans/2026-10-02-checkpoints-contract.md §5-§7): timeline texts for
// the six new events, the "paused until …" / "exhausted until …" labels,
// which recovery actions the run page offers, the handoff-chain summary of a
// task step and the role's `onQuota` editing. No React here, so it's unit
// tested on its own (run-recovery.test.ts).

import {
  RunStatus,
  type ConnectionExhaustion,
  type ConnectionExhaustedReason,
  type HandoffCandidate,
  type OnQuotaSettings,
  type Run,
  type RunCheckpointEvent,
  type RunRecoveryState,
  type RunnerFailureKind,
  type TaskRunAttempt,
} from '@goblic/shared'
import { ApiError } from './api-client'
import { formatMs } from './format'

// ---------------------------------------------------------------------------
// Times
// ---------------------------------------------------------------------------

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/** "15:00" today, "Fri 15:00" within a week, else "2 Oct, 15:00". */
export function formatUntil(value: string | null | undefined, now: Date = new Date()): string {
  const d = toDate(value)
  if (!d) return '—'
  const clock = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  if (sameDay(d, now)) return clock
  const days = Math.abs(d.getTime() - now.getTime()) / 86_400_000
  if (days < 6) {
    return `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${clock}`
  }
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}, ${clock}`
}

/** A Date as the value of an `<input type="datetime-local">` (local time, minutes). */
export function toDatetimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** The `datetime-local` value back to an ISO timestamp (UTC), or null when empty/invalid. */
export function fromDatetimeLocal(value: string): string | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

// ---------------------------------------------------------------------------
// Run status
// ---------------------------------------------------------------------------

/** Paused, but resumes by itself (usage limit, or "Resume at …"). */
export function isScheduledResume(run: Pick<Run, 'status' | 'resumeAfter'>): boolean {
  return run.status === RunStatus.Paused && run.resumeAfter != null
}

/**
 * Waiting on a person: an `awaiting_approval` run, or a pause without
 * `resumeAfter` (an ask/approval pause). A quota-paused run resumes by itself
 * and is not "waiting for you" (contract §7).
 */
export function isWaitingOnPerson(run: Pick<Run, 'status' | 'resumeAfter'>): boolean {
  if (run.status === RunStatus.AwaitingApproval) return true
  return run.status === RunStatus.Paused && run.resumeAfter == null
}

/** Badge label override for a run, or undefined for the plain status label. */
export function runStatusLabel(
  run: Pick<Run, 'status' | 'resumeAfter'>,
  now: Date = new Date()
): string | undefined {
  if (isScheduledResume(run)) return `Paused until ${formatUntil(run.resumeAfter, now)}`
  return undefined
}

// ---------------------------------------------------------------------------
// Failures and connection exhaustion
// ---------------------------------------------------------------------------

export const FAILURE_KIND_LABEL: Record<RunnerFailureKind, string> = {
  transient: 'API error',
  rate_limit: 'Rate limit',
  quota: 'Usage limit',
  auth: 'Login no longer valid',
  fatal: 'Runner error',
}

export const HANDOFF_REASON_LABEL: Record<'quota' | 'manual', string> = {
  quota: 'usage limit',
  manual: 'handed off by hand',
}

export interface ExhaustionBadgeInfo {
  label: string
  /** The label mid-sentence ("exhausted until 3:00 PM"). */
  inline: string
  tone: 'warning' | 'danger'
  /** Longer text for a tooltip. */
  title: string
  reason: ConnectionExhaustedReason
}

/**
 * "Exhausted until 15:00" (quota / rate limit, while in the future) or
 * "Sign in again" (auth, until a new login). Null when the connection is
 * usable — an `exhaustedUntil` in the past no longer counts.
 */
export function exhaustionBadge(
  exhaustion: Partial<ConnectionExhaustion> | null | undefined,
  now: Date = new Date()
): ExhaustionBadgeInfo | null {
  if (!exhaustion?.exhaustedReason) return null
  if (exhaustion.exhaustedReason === 'auth') {
    return {
      label: 'Sign in again',
      inline: 'sign in again',
      tone: 'danger',
      title: 'The login stopped working. Runs on it wait until it’s logged in again.',
      reason: 'auth',
    }
  }
  const until = toDate(exhaustion.exhaustedUntil)
  if (!until || until.getTime() <= now.getTime()) return null
  const what = exhaustion.exhaustedReason === 'rate_limit' ? 'Rate limited' : 'Usage limit reached'
  const label = `Exhausted until ${formatUntil(exhaustion.exhaustedUntil, now)}`
  return {
    label,
    inline: `e${label.slice(1)}`,
    tone: 'warning',
    title: `${what}. No new runs start on it until ${until.toLocaleString()}.`,
    reason: exhaustion.exhaustedReason,
  }
}

// ---------------------------------------------------------------------------
// Timeline events (contract §6)
// ---------------------------------------------------------------------------

export interface CheckpointEventDescription {
  text: string
  detail?: string
  tone?: 'danger' | 'signal' | 'ok'
  /** Another run of the chain the event points at. */
  runLink?: { runId: string; label: string }
}

export function isCheckpointEvent(event: { type: string }): event is RunCheckpointEvent {
  return (
    event.type === 'runner_failure' ||
    event.type === 'runner_retry' ||
    event.type === 'checkpoint_saved' ||
    event.type === 'quota_exhausted' ||
    event.type === 'handed_off' ||
    event.type === 'resumed'
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const TRIGGER_LABEL: Record<string, string> = {
  interval: 'every 5 minutes',
  report_progress: 'after a progress report',
  finish: 'when the agent finished',
  commit: 'after a commit',
  pause: 'on pause',
  failure: 'when the run stopped',
  shutdown: 'on shutdown',
}

const RESUME_VIA_LABEL: Record<string, string> = {
  approval_decision: 'after your decision',
  quota_wait: 'after the usage limit reset',
  crash: 'after the worker stopped',
  manual: 'by hand',
  handoff: 'as a handoff',
}

/**
 * One line per checkpoint event for the Activity tab.
 * `agentName` resolves agent ids (for `quota_exhausted.toAgentId`).
 */
export function describeCheckpointEvent(
  event: RunCheckpointEvent,
  agentName: (id: string | null | undefined) => string = (id) => id ?? '—',
  now: Date = new Date()
): CheckpointEventDescription {
  switch (event.type) {
    case 'runner_failure':
      return {
        text: `${FAILURE_KIND_LABEL[event.failure.kind]}: ${event.failure.message}`,
        detail:
          [
            event.failure.provider,
            event.failure.resetsAt ? `resets ${formatUntil(event.failure.resetsAt, now)}` : null,
            event.checkpointId ? 'checkpoint saved' : null,
          ]
            .filter(Boolean)
            .join(' · ') || undefined,
        tone: event.failure.kind === 'transient' || event.failure.kind === 'rate_limit' ? 'signal' : 'danger',
      }
    case 'runner_retry':
      return {
        text: `Retrying after ${event.kind === 'rate_limit' ? 'a rate limit' : 'an API error'} (attempt ${event.attempt} of ${event.maxAttempts}) in ${formatMs(event.delayMs)}`,
        detail: [
          event.resumeMode === 'session' ? 'continues the same conversation' : 'continues from a resume prompt',
          event.message || null,
        ]
          .filter(Boolean)
          .join(' · '),
        tone: 'signal',
      }
    case 'checkpoint_saved':
      if (event.skipped === 'too_large') {
        return {
          text: 'Checkpoint skipped: the changes are too large to save',
          detail: `${formatBytes(event.patchBytes)} patch`,
          tone: 'signal',
        }
      }
      return {
        text: `Checkpoint saved ${TRIGGER_LABEL[event.trigger] ?? event.trigger}`,
        detail: `${formatBytes(event.patchBytes)} changes · ${formatBytes(event.sessionBytes)} conversation`,
      }
    case 'quota_exhausted': {
      const where = event.provider ? ` on ${event.provider}` : ''
      const until = event.exhaustedUntil ? ` until ${formatUntil(event.exhaustedUntil, now)}` : ''
      if (event.decision === 'wait') {
        return {
          text: `Usage limit reached${where}. Waiting${event.resumeAfter ? ` until ${formatUntil(event.resumeAfter, now)}` : until}`,
          detail: event.message || undefined,
          tone: 'signal',
        }
      }
      if (event.decision === 'handoff') {
        return {
          text: `Usage limit reached${where}. Handing off to ${agentName(event.toAgentId)}`,
          detail: event.message || undefined,
          tone: 'signal',
          runLink: event.childRunId ? { runId: event.childRunId, label: 'Open new run' } : undefined,
        }
      }
      return {
        text: `Usage limit reached${where}${until}. No other agent could take over`,
        detail: event.message || undefined,
        tone: 'danger',
      }
    }
    case 'handed_off':
      return {
        text: `Handed off to ${event.toAgentName} (${event.reason === 'quota' ? 'usage limit' : 'by hand'})`,
        detail: `Handoff ${event.handoffNumber}${event.checkpointId ? ' · with its checkpoint' : ' · from the start'}`,
        tone: 'signal',
        runLink: { runId: event.childRunId, label: 'Open new run' },
      }
    case 'resumed':
      if (event.via === 'handoff') {
        return {
          text: 'Took over the work of an earlier run',
          detail: event.patchRestored ? 'Its changes were restored' : 'No changes to restore',
          tone: 'ok',
          runLink: event.fromRunId ? { runId: event.fromRunId, label: 'Open earlier run' } : undefined,
        }
      }
      return {
        text: `Resumed ${RESUME_VIA_LABEL[event.via] ?? event.via}`,
        detail: [
          event.mode === 'session' ? 'same conversation' : 'from a resume prompt',
          event.patchRestored ? 'changes restored' : null,
        ]
          .filter(Boolean)
          .join(' · '),
        tone: 'ok',
      }
  }
}

// ---------------------------------------------------------------------------
// Recovery actions on the run page (contract §5-G, §7)
// ---------------------------------------------------------------------------

export interface RecoveryView {
  /** Show the panel at all. */
  visible: boolean
  resumeNow: { enabled: boolean; reason?: string }
  resumeAt: { enabled: boolean; reason?: string }
  handoff: { enabled: boolean; reason?: string }
  /** Candidates, available first (the role's fallbacks in order, then others). */
  candidates: HandoffCandidate[]
}

const RECOVERABLE: readonly string[] = [RunStatus.Paused, RunStatus.Failed, RunStatus.Blocked]

export function recoveryView(state: RunRecoveryState | null | undefined): RecoveryView {
  const none = { enabled: false }
  if (!state || !RECOVERABLE.includes(state.status)) {
    return { visible: false, resumeNow: none, resumeAt: none, handoff: none, candidates: [] }
  }
  const noCheckpoint = state.checkpoint ? undefined : 'No checkpoint to resume from.'
  const candidates = [...state.handoffCandidates].sort((a, b) => {
    if (a.available !== b.available) return a.available ? -1 : 1
    if (a.fallback !== b.fallback) return a.fallback ? -1 : 1
    return 0
  })
  const anyAvailable = candidates.some((c) => c.available)
  const resumeNow = state.actions.resumeNow
    ? { enabled: true }
    : { enabled: false, reason: noCheckpoint ?? 'This run can’t be resumed now.' }
  const resumeAt = state.actions.resumeAt
    ? { enabled: true }
    : { enabled: false, reason: noCheckpoint ?? 'This run can’t be scheduled.' }
  const handoff = !state.actions.handoff
    ? { enabled: false, reason: 'This run can’t be handed off (no checkpoint, or the handoff limit is reached).' }
    : !anyAvailable
      ? { enabled: false, reason: 'No other agent with this role is available.' }
      : { enabled: true }
  const visible =
    state.actions.resumeNow ||
    state.actions.resumeAt ||
    state.actions.handoff ||
    state.resumeAfter != null ||
    state.failure != null ||
    state.checkpoint != null
  return { visible, resumeNow, resumeAt, handoff, candidates }
}

/** Why a candidate can't take over, or null when it can. */
export function candidateUnavailableReason(
  candidate: HandoffCandidate,
  now: Date = new Date()
): string | null {
  if (candidate.available) return null
  const badge = exhaustionBadge(
    { exhaustedUntil: candidate.exhaustedUntil, exhaustedReason: candidate.exhaustedReason },
    now
  )
  if (badge) return badge.inline
  return 'not available'
}

/** Error text for a failed resume/hand-off request. */
export function recoveryErrorMessage(error: unknown, now: Date = new Date()): string {
  if (error instanceof ApiError) {
    // The API words times as ISO timestamps ("…until 2026-10-03T14:28:04.183Z"): show them as the page does.
    const message = error.message.replace(
      /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g,
      (iso) => formatUntil(iso, now)
    )
    if (error.status === 409) {
      return `This run can’t be resumed or handed off anymore${message ? `: ${message}` : '.'} The workflow may have moved on; reload to see where it is.`
    }
    if (error.status === 422) {
      return message || 'That agent can’t take over this run.'
    }
    if (error.status === 404) return 'This run no longer exists.'
    if (error.status === 501) return 'The server can’t resume or hand off runs yet.'
    return error.message
  }
  return error instanceof Error ? error.message : 'Something went wrong.'
}

// ---------------------------------------------------------------------------
// Handoff chain (task steps, run page)
// ---------------------------------------------------------------------------

/**
 * "Claude → GPT-5 · usage limit": the agents of a chain, oldest first, and
 * why the work moved. `current` is the step's own (latest) agent.
 */
export function chainSummary(
  previous: Pick<TaskRunAttempt, 'agentName' | 'handoffReason'>[],
  current: string | null
): string {
  const names = [...previous.map((a) => a.agentName ?? 'Agent'), current ?? 'Agent']
  const reasons = [
    ...new Set(
      previous
        .map((a) => a.handoffReason)
        .filter((r): r is 'quota' | 'manual' => r != null)
        .map((r) => HANDOFF_REASON_LABEL[r])
    ),
  ]
  return `${names.join(' → ')}${reasons.length ? ` · ${reasons.join(', ')}` : ''}`
}

// ---------------------------------------------------------------------------
// Role settings: onQuota (contract §3.5)
// ---------------------------------------------------------------------------

export const ON_QUOTA_MODE_OPTIONS: { value: OnQuotaSettings['mode']; label: string; hint: string }[] = [
  {
    value: 'wait',
    label: 'Wait',
    hint: 'Pause until the limit resets, then continue with the same agent and conversation.',
  },
  {
    value: 'handoff',
    label: 'Hand off',
    hint: 'Another agent takes over right away. If none is available, the run is blocked.',
  },
  {
    value: 'handoff_then_wait',
    label: 'Hand off, else wait',
    hint: 'Another agent takes over when one is available; otherwise wait for the reset.',
  },
]

export const MAX_FALLBACK_AGENTS = 5

/** Moves item `index` one place up (-1) or down (+1); out-of-range moves are no-ops. */
export function moveItem<T>(list: readonly T[], index: number, direction: -1 | 1): T[] {
  const target = index + direction
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return [...list]
  const next = [...list]
  const [item] = next.splice(index, 1)
  next.splice(target, 0, item as T)
  return next
}

/** Adds an agent at the end (once, at most MAX_FALLBACK_AGENTS). */
export function addFallback(list: readonly string[], agentId: string): string[] {
  if (list.includes(agentId) || list.length >= MAX_FALLBACK_AGENTS) return [...list]
  return [...list, agentId]
}

/** Without fallback agents every mode behaves as `wait` (contract §3.5). */
export function onQuotaEffectiveNote(settings: OnQuotaSettings): string | null {
  if (settings.mode !== 'wait' && settings.fallbackAgentIds.length === 0) {
    return 'Without fallback agents this behaves as Wait.'
  }
  return null
}
